import { useMemo } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { encodeQr, qrSvgPath } from "@/lib/qr";

const MARGIN = 4;

interface QrCodeProps {
  readonly value: string;
  readonly label: string;
}

/** QR Code em SVG gerado localmente, com download do arquivo .svg. */
export function QrCode({ value, label }: QrCodeProps): JSX.Element {
  const qr = useMemo(() => {
    try {
      const matrix = encodeQr(value, { ecc: "M" });
      return { path: qrSvgPath(matrix, MARGIN), viewBox: matrix.size + MARGIN * 2 };
    } catch {
      return null;
    }
  }, [value]);

  if (!qr) {
    return <p className="text-xs text-destructive">URL longa demais para virar QR Code.</p>;
  }

  const svgMarkup = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${qr.viewBox} ${qr.viewBox}" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="#fff"/><path d="${qr.path}" fill="#000"/></svg>`;

  const handleDownload = (): void => {
    const href = URL.createObjectURL(new Blob([svgMarkup], { type: "image/svg+xml" }));
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = "qrcode-utm.svg";
    anchor.click();
    URL.revokeObjectURL(href);
  };

  return (
    <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
      <svg
        role="img"
        aria-label={label}
        viewBox={`0 0 ${qr.viewBox} ${qr.viewBox}`}
        shapeRendering="crispEdges"
        className="h-40 w-40 shrink-0 rounded-lg border border-border bg-white p-1"
      >
        <rect width="100%" height="100%" fill="#fff" />
        <path d={qr.path} fill="#000" />
      </svg>
      <div className="space-y-2 text-center sm:text-left">
        <p className="text-sm text-muted-foreground">Aponte a câmera do celular para abrir o link com as UTMs.</p>
        <Button type="button" size="sm" variant="secondary" onClick={handleDownload}>
          <Download size={14} />
          Baixar QR Code
        </Button>
      </div>
    </div>
  );
}
